import { useNavigate, useParams } from "react-router-dom";
import Sidebar from "../../components/sidebar";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { useState, useEffect } from "react";
import AdModel from "../../domain/models/ad.model";
import AuspicienteModel from "../../domain/models/auspiciante.model";
import useAdGeneralConf from "../../hooks/ad-hooks/useAdGeneralConf.hook";
import { getS3Url } from "../../shared/static/constants.static";
import SponsorModal from "../../components/SponsorModal";
import useSponsorGeneralConf from "../../hooks/sponsor-hooks/useSponsorGeneralConf.hook";

// Esquema de validación
const getValidationSchema = (isEditing: boolean, currentAd: AdModel | null) => Yup.object({
  nombre: Yup.string()
    .required("El nombre es obligatorio")
    .min(2, "El nombre debe tener al menos 2 caracteres"),
  descripcion: Yup.string()
    .required("La descripción es obligatoria")
    .min(5, "La descripción debe tener al menos 5 caracteres"),
  url: Yup.string()
    .url("Debe ser una URL válida")
    .required("La URL es obligatoria"),
  categoria: Yup.string()
    .required("La categoría es obligatoria"),
  auspiciante: Yup.number()
    .required("El auspiciante es obligatorio")
    .min(1, "Debe seleccionar un auspiciante"),
  peso: Yup.number()
    .required("El peso es obligatorio")
    .min(1, "El peso debe ser mayor a 0")
    .max(100, "El peso no puede ser mayor a 100"),
  activo: Yup.boolean(),
  rutaImagen: isEditing 
    ? Yup.mixed().test(
        'image-required-for-editing',
        'La imagen es obligatoria',
        function(value) {
          // Si estamos editando y hay una imagen actual, no necesitamos nueva imagen
          if (currentAd?.rutaImagen) {
            return true;
          }
          // Si no hay imagen actual, necesitamos una nueva
          return value != null;
        }
      )
    : Yup.mixed().required("La imagen es obligatoria") // Obligatorio al crear
});

const AdForm = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { loaders, data, actions } = useAdGeneralConf();
  const { data: sponsorData, actions: sponsorActions, loaders: sponsorLoaders } = useSponsorGeneralConf();
  
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string>("");
  const [showAuspicianteModal, setShowAuspicianteModal] = useState(false);
  const [auspiciantes, setAuspiciantes] = useState<AuspicienteModel[]>([]);
  const [currentAd, setCurrentAd] = useState<AdModel | null>(null);
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    sponsorActions.fetchSponsors();
  }, []);

  useEffect(() => {
    setAuspiciantes(sponsorData.sponsors);
  }, [sponsorData.sponsors]);

  // Cargar datos del anuncio si estamos editando
  useEffect(() => {
    if (id) {
      setIsEditing(true);
      // Solo ejecutar si no tenemos el anuncio actual cargado
      if (!currentAd || currentAd.id !== parseInt(id)) {
        actions.fetchAds().then(() => {
          const adToEdit = data.ads.find(ad => ad.id === parseInt(id));
          if (adToEdit) {
            setCurrentAd(adToEdit);
            // Si el anuncio tiene imagen, mostrar preview
            if (adToEdit.rutaImagen) {
              setImagePreview(adToEdit.rutaImagen);
            }
            // Agregar el auspiciante del anuncio a la lista si no existe
            const auspicianteExists = auspiciantes.find(a => a.id === adToEdit.auspiciante.id);
            if (!auspicianteExists) {
              setAuspiciantes(prev => [...prev, adToEdit.auspiciante]);
            }
          }
        });
      }
    }
  }, [id]);

  useEffect(() => {
    if (id && isEditing && data.ads.length > 0 && !currentAd) {
      const adToEdit = data.ads.find(ad => ad.id === parseInt(id));
      if (adToEdit) {
        setCurrentAd(adToEdit);
        // Si el anuncio tiene imagen, mostrar preview
        if (adToEdit.rutaImagen) {
          setImagePreview(adToEdit.rutaImagen);
        }
        // Agregar el auspiciante del anuncio a la lista si no existe
        const auspicianteExists = auspiciantes.find(a => a.id === adToEdit.auspiciante.id);
        if (!auspicianteExists) {
          setAuspiciantes(prev => [...prev, adToEdit.auspiciante]);
        }
      }
    }
  }, [data.ads, id, isEditing, currentAd, auspiciantes]);

  const goBack = () => {
    navigate(-1);
  };

  const categorias = [
    { value: "alimentacion", label: "Alimentación" },
    { value: "crecimiento", label: "Crecimiento" },
    { value: "denticion", label: "Dentición" },
    { value: "psicomotriz", label: "Desarrollo Psicomotriz" },
    { value: "vacunas", label: "Vacunas" },
  ];

  const initialValues = {
    nombre: currentAd?.nombre || "",
    descripcion: currentAd?.descripcion || "",
    url: currentAd?.url || "",
    categoria: currentAd?.categoria || "",
    auspiciante: currentAd?.auspiciante?.id || "",
    peso: currentAd?.peso || 1,
    activo: currentAd?.activo ?? true,
    rutaImagen: isEditing ? undefined : null // undefined para edición, null para creación
  };

  const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>, setFieldValue: any) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedImage(file);
      setFieldValue("rutaImagen", file);
      
      // Crear preview de la imagen
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveImage = (setFieldValue: any) => {
    setSelectedImage(null);
    setFieldValue("rutaImagen", null);
    
    // Si estamos editando, restaurar la imagen original, si no, limpiar completamente
    if (isEditing && currentAd?.rutaImagen) {
      setImagePreview(currentAd.rutaImagen);
    } else {
      setImagePreview("");
    }
    
    const fileInput = document.getElementById("rutaImagen") as HTMLInputElement;
    if (fileInput) {
      fileInput.value = "";
    }
  };

  const openAuspicianteModal = () => {
    setShowAuspicianteModal(true);
  };

  const closeAuspicianteModal = () => {
    setShowAuspicianteModal(false);
  };

  const handleCreateSponsor = async (sponsor: AuspicienteModel) => {
    await sponsorActions.createSponsorHandler(sponsor);
    // El nuevo auspiciante se agregará automáticamente a través del useEffect que escucha sponsorData.sponsors
  };

  const handleSubmit = async (values: any) => {
    try {

      let rutaImagen = "";

      // Si estamos editando y no se seleccionó nueva imagen, usar la existente
      if (isEditing && !selectedImage && currentAd?.rutaImagen) {
        rutaImagen = currentAd.rutaImagen;
      }

      // Si se seleccionó una nueva imagen, subirla
      else if (selectedImage) {
        const uploadedImage = await actions.uploadAdImageHandler(selectedImage);
        
        const s3Url = getS3Url();
        
        rutaImagen = (s3Url || "") + (uploadedImage.fileName || "");
      }

      // Validar que hay una imagen
      if (!rutaImagen || rutaImagen.trim() === "") {
        throw new Error("La imagen es obligatoria");
      }

      const aus = auspiciantes.find(a => a.id === Number(values.auspiciante));
      if (!aus) {
        throw new Error("El auspiciante seleccionado no es válido");
      }

      const auspiciante: AuspicienteModel = {
        id: aus.id,
        nombre: aus.nombre
      };

      const ad: AdModel = {
        id: isEditing ? currentAd?.id : undefined,
        nombre: values.nombre,
        descripcion: values.descripcion,
        url: values.url,
        categoria: values.categoria,
        auspiciante: auspiciante,
        peso: values.peso,
        activo: values.activo,
        rutaImagen: rutaImagen,
      };

      if (isEditing) {
        await actions.updateAdHandler(ad);
      } else {
        await actions.createAdHandler(ad);
      }

      navigate(-1);
    } catch (error) {
      console.error(`Error al ${isEditing ? 'actualizar' : 'crear'} el anuncio:`, error);
    }
  };

  return (
    <div className="background-colored">
      <div className="row justify-content-center">
        <div className="col-12 col-sm-2 justify-content-center sidebar">
          <Sidebar />
        </div>
        <div className="col-12 col-sm-9 justify-content-center datatable-container ml">
          <div className="header-container">
            <div>
              {/* <a className="arrow" onClick={goBack}>
                <i className="fa fa-arrow-left" aria-hidden="true"></i>
              </a> */}
              <span id="customized" className="header-label">
                {isEditing ? "Editar Anuncio" : "Crear Nuevo Anuncio"}{" "}
              </span>
            </div>
          </div>
          <div className="mt-4 scroll-container" >
            <Formik
              key={currentAd?.id || 'new'} // Reinicia el formulario cuando cambia el anuncio
              initialValues={initialValues}
              validationSchema={getValidationSchema(isEditing, currentAd)}
              onSubmit={handleSubmit}
              enableReinitialize={true} // Permite reinicializar cuando cambian los initialValues
            >
              {({ setFieldValue, values, isSubmitting }) => (
                <Form>
                  <div className="row" style={{ maxWidth: "100%" }}>
                    {/* Nombre del Anuncio */}
                    <div className="col-md-6 mb-3">
                      <label htmlFor="nombre" className="form-label">
                        Nombre del Anuncio *
                      </label>
                      <Field
                        type="text"
                        className="form-control"
                        id="nombre"
                        name="nombre"
                        placeholder="Ingrese el nombre del anuncio"
                      />
                      <ErrorMessage name="nombre" component="div" className="text-danger" />
                    </div>

                    {/* Categoría */}
                    <div className="col-md-6 mb-3">
                      <label htmlFor="categoria" className="form-label">
                        Categoría *
                      </label>
                      <Field as="select" className="form-control" id="categoria" name="categoria">
                        <option value="">Seleccione una categoría</option>
                        {categorias.map((cat) => (
                          <option key={cat.value} value={cat.value}>
                            {cat.label}
                          </option>
                        ))}
                      </Field>
                      <ErrorMessage name="categoria" component="div" className="text-danger" />
                    </div>

                    {/* Auspiciante */}
                    <div className="col-md-6 mb-3">
                      <label htmlFor="auspiciante" className="form-label">
                        Auspiciante *
                      </label>
                      <div className="d-flex gap-2">
                        <Field as="select" className="form-control" id="auspiciante" name="auspiciante">
                          <option value="">Seleccione un auspiciante</option>
                          {auspiciantes.map((ausp) => (
                            <option key={ausp.id} value={ausp.id}>
                              {ausp.nombre}
                            </option>
                          ))}
                        </Field>
                        <button
                          type="button"
                          className="btn btn-success btn-sm"
                          onClick={openAuspicianteModal}
                          title="Agregar nuevo auspiciante"
                          style={{ minWidth: "40px", height: "38px" }}
                        >
                          <i className="fa fa-plus"></i>
                        </button>
                      </div>
                      <ErrorMessage name="auspiciante" component="div" className="text-danger" />
                    </div>

                    {/* Peso */}
                    <div className="col-md-6 mb-3">
                      <label htmlFor="peso" className="form-label">
                        Peso (Prioridad) *
                      </label>
                      <Field
                        type="number"
                        className="form-control"
                        id="peso"
                        name="peso"
                        min="1"
                        max="100"
                        placeholder="1-100"
                      />
                      <ErrorMessage name="peso" component="div" className="text-danger" />
                      <small className="form-text text-muted">
                        Menor peso = mayor prioridad de visualización
                      </small>
                    </div>

                    {/* URL */}
                    <div className="col-12 mb-3">
                      <label htmlFor="url" className="form-label">
                        URL de Destino *
                      </label>
                      <Field
                        type="url"
                        className="form-control"
                        id="url"
                        name="url"
                        placeholder="https://ejemplo.com"
                      />
                      <ErrorMessage name="url" component="div" className="text-danger" />
                    </div>

                    {/* Descripción */}
                    <div className="col-12 mb-3">
                      <label htmlFor="descripcion" className="form-label">
                        Descripción *
                      </label>
                      <Field
                        as="textarea"
                        className="form-control"
                        id="descripcion"
                        name="descripcion"
                        rows={3}
                        placeholder="Descripción del anuncio"
                      />
                      <ErrorMessage name="descripcion" component="div" className="text-danger" />
                    </div>

                    {/* Imagen */}
                    <div className="col-12 mb-3">
                      <label htmlFor="rutaImagen" className="form-label">
                        Imagen del Anuncio {!isEditing && "*"}
                      </label>
                      {isEditing && (
                        <small className="form-text text-muted d-block mb-2">
                          Deja en blanco para mantener la imagen actual
                        </small>
                      )}
                      <input
                        type="file"
                        className="form-control"
                        id="rutaImagen"
                        name="rutaImagen"
                        accept="image/*"
                        onChange={(event) => handleImageChange(event, setFieldValue)}
                      />
                      <ErrorMessage name="rutaImagen" component="div" className="text-danger" />
                      
                      {/* Preview de la imagen */}
                      {imagePreview && (
                        <div className="mt-3">
                          <div className="d-flex justify-content-between align-items-center mb-2">
                            <label className="form-label mb-0">Vista previa:</label>
                            <button
                              type="button"
                              className="btn btn-danger btn-sm"
                              onClick={() => handleRemoveImage(setFieldValue)}
                              title={isEditing ? "Restaurar imagen original" : "Eliminar imagen"}
                            >
                              <i className="fa fa-trash me-1"></i>
                              {isEditing ? "Restaurar" : "Eliminar"}
                            </button>
                          </div>
                          <div className="border p-2" style={{ maxWidth: "300px" }}>
                            <img
                              src={imagePreview}
                              alt="Preview"
                              className="img-fluid"
                              style={{ maxHeight: "200px", objectFit: "contain" }}
                            />
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Estado Activo */}
                    <div className="col-12 mb-4">
                      <div className="form-check">
                        <Field
                          type="checkbox"
                          className="form-check-input"
                          id="activo"
                          name="activo"
                        />
                        <label className="form-check-label" htmlFor="activo">
                          Anuncio activo
                        </label>
                      </div>
                      <small className="form-text text-muted">
                        Solo los anuncios activos se mostrarán en la aplicación
                      </small>
                    </div>

                    {/* Botones */}
                    <div className="col-12">
                      <div className="d-flex gap-2">
                        <button
                          type="submit"
                          className="btn btn-primary-custom"
                          disabled={isSubmitting}
                        >
                          {isSubmitting ? (
                            <>
                              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                              {isEditing ? "Actualizando..." : "Creando..."}
                            </>
                          ) : (
                            isEditing ? "Actualizar Anuncio" : "Crear Anuncio"
                          )}
                        </button>
                        <button
                          type="button"
                          className="btn btn-secondary"
                          onClick={goBack}
                        >
                          Cancelar
                        </button>
                      </div>
                    </div>
                  </div>
                </Form>
              )}
            </Formik>
          </div>
        </div>
      </div>

      {/* Modal para agregar nuevo auspiciante */}
      <SponsorModal
        isOpen={showAuspicianteModal}
        onClose={closeAuspicianteModal}
        onSave={handleCreateSponsor}
        sponsor={null} // Solo para crear, no para editar
        isLoading={sponsorLoaders.isLoadingData}
      />
    </div>
  );
};

export default AdForm;